import React, {useState} from 'react';
import {useParams, Link, useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import {useCourse} from '../hooks/useCourses';
import {useEnrollments} from '../hooks/useEnrollments';
import {useStripeCheckout} from '../hooks/useStripe';
import {useAuth} from '../contexts/AuthContext';
import {useAnalytics} from '../hooks/useAnalytics';
import SEOHead from '../components/SEOHead';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import CouponInput from '../components/checkout/CouponInput';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiPlay, FiClock, FiUsers, FiAward, FiCheck, FiLock, FiArrowLeft, FiCreditCard, FiDollarSign} = FiIcons;

const CourseDetail = () => {
  const {slug} = useParams();
  const navigate = useNavigate();
  const {user} = useAuth();
  const {course, modules, loading} = useCourse(slug);
  const {enrollInCourse, isEnrolled, loading: enrollmentLoading} = useEnrollments();
  const {createCheckoutSession, loading: checkoutLoading} = useStripeCheckout();
  const {trackCTAClick, trackCourseInteraction} = useAnalytics();
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <SEOHead
          title="Course Not Found | Empower Nutrition"
          description="The course you're looking for could not be found."
        />
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Course not found</h1>
          <Link to="/courses" className="text-nutrition-600 hover:text-nutrition-700">
            Back to courses
          </Link>
        </div>
      </div>
    );
  }

  const isUserEnrolled = user && isEnrolled(course.id);
  const totalLessons = modules.reduce((acc, module) => acc + (module.lessons?.length || 0), 0);
  const isFree = course.price === 0;

  // Calculate final price with coupon
  const originalPrice = parseFloat(course.price) || 0;
  const discountAmount = appliedCoupon?.discountAmount || 0;
  const finalPrice = Math.max(0, originalPrice - discountAmount);

  const handleEnroll = async () => {
    if (!user) {
      trackCTAClick('sign_in_to_enroll', 'course_detail', '/login');
      navigate('/login', {state: {from: {pathname: `/courses/${slug}`}}});
      return;
    }

    if (isFree) {
      trackCourseInteraction('enroll_free', course.id, course.title);
      await enrollInCourse(course.id);
    } else {
      trackCTAClick('enroll_paid_course', 'course_detail', 'stripe_checkout');
      trackCourseInteraction('start_checkout', course.id, course.title);
      await createCheckoutSession(course.id, appliedCoupon?.code);
    }
  };

  const handleContinueLearning = () => {
    trackCTAClick('continue_learning', 'course_detail', `/course/${course.slug}`);
    trackCourseInteraction('continue_course', course.id, course.title);
  };

  const handleCouponApplied = (couponData) => {
    setAppliedCoupon(couponData);
    trackCourseInteraction('coupon_applied', course.id, course.title);
  };

  const handleCouponRemoved = () => {
    setAppliedCoupon(null);
    trackCourseInteraction('coupon_removed', course.id, course.title);
  };

  // SEO data
  const seoTitle = `${course.title} | Online Course | Empower Nutrition`;
  const seoDescription = course.summary || `Learn ${course.title} with our comprehensive online course. Expert-led instruction with lifetime access and practical resources.`;
  const seoImage = course.cover_image_url || "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1200&h=630&fit=crop";

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={`${course.title}, nutrition course, online learning, ${course.title.toLowerCase()}`}
        url={`/courses/${slug}`}
        image={seoImage}
        type="article"
        publishedTime={course.created_at}
        modifiedTime={course.updated_at}
      />

      {/* Hero Section */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link 
            to="/courses" 
            className="inline-flex items-center space-x-2 text-nutrition-600 hover:text-nutrition-700 mb-6"
            onClick={() => trackCTAClick('back_to_courses', 'course_detail', '/courses')}
          >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
            <span>Back to courses</span>
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Course Info */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.8}}
              >
                <h1 className="text-4xl font-bold text-gray-900 mb-4">
                  {course.title}
                </h1>
                {course.summary && (
                  <p className="text-xl text-gray-600 mb-6">
                    {course.summary}
                  </p>
                )}

                <div className="flex items-center space-x-6 text-gray-500 mb-8">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiClock} className="w-5 h-5" />
                    <span>Self-paced</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiUsers} className="w-5 h-5" />
                    <span>All levels</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiAward} className="w-5 h-5" />
                    <span>Certificate</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiDollarSign} className="w-5 h-5" />
                    <span>{isFree ? 'Free' : `$${originalPrice}`}</span>
                  </div>
                </div>

                {/* Course Content */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Course Content</h2>
                  <div className="space-y-4">
                    {modules.map((module, moduleIndex) => (
                      <div key={module.id} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-6 py-4">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Module {moduleIndex + 1}: {module.title}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {module.lessons?.length || 0} lessons
                          </p>
                        </div>
                        <div className="px-6 py-4">
                          <div className="space-y-3">
                            {module.lessons?.map((lesson, lessonIndex) => (
                              <div key={lesson.id} className="flex items-center space-x-3">
                                <div className="flex-shrink-0">
                                  {isUserEnrolled ? (
                                    <SafeIcon icon={FiPlay} className="w-4 h-4 text-nutrition-600" />
                                  ) : (
                                    <SafeIcon icon={FiLock} className="w-4 h-4 text-gray-400" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <p className={`text-sm ${isUserEnrolled ? 'text-gray-900' : 'text-gray-500'}`}>
                                    {lessonIndex + 1}. {lesson.title}
                                  </p>
                                  {lesson.estimated_minutes > 0 && (
                                    <p className="text-xs text-gray-400">
                                      {lesson.estimated_minutes} min
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Enrollment Card */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{opacity: 0, x: 50}}
                animate={{opacity: 1, x: 0}}
                transition={{duration: 0.8, delay: 0.2}}
                className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 sticky top-8"
              >
                {course.cover_image_url && (
                  <img
                    src={course.cover_image_url}
                    alt={course.title}
                    className="w-full h-48 object-cover rounded-lg mb-6"
                  />
                )}

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Price</span>
                    <div className="text-right">
                      {appliedCoupon && originalPrice > 0 && (
                        <div className="text-sm text-gray-500 line-through">
                          ${originalPrice.toFixed(2)}
                        </div>
                      )}
                      <span className="text-2xl font-bold text-gray-900">
                        {finalPrice > 0 ? `$${finalPrice.toFixed(2)}` : 'Free'}
                      </span>
                      {appliedCoupon && (
                        <div className="text-sm text-green-600 font-medium">
                          Save ${discountAmount.toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Lessons</span>
                    <span className="font-semibold text-gray-900">{totalLessons}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Access</span>
                    <span className="font-semibold text-gray-900">Lifetime</span>
                  </div>
                </div>

                {/* Coupon Input for Paid Courses */}
                {!isFree && !isUserEnrolled && user && (
                  <div className="mb-6">
                    <CouponInput
                      coursePrice={originalPrice}
                      onCouponApplied={handleCouponApplied}
                      onCouponRemoved={handleCouponRemoved}
                    />
                  </div>
                )}

                {isUserEnrolled ? (
                  <Link
                    to={`/course/${course.slug}`}
                    onClick={handleContinueLearning}
                    className="w-full bg-nutrition-600 hover:bg-nutrition-700 text-white py-3 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
                  >
                    <span>Continue Learning</span>
                    <SafeIcon icon={FiPlay} className="w-4 h-4" />
                  </Link>
                ) : (
                  <>
                    {user ? (
                      <button
                        onClick={handleEnroll}
                        disabled={enrollmentLoading || checkoutLoading}
                        className="w-full bg-nutrition-600 hover:bg-nutrition-700 text-white py-3 px-4 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                      >
                        {enrollmentLoading || checkoutLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <>
                            {isFree ? (
                              <>
                                <span>Enroll Free</span>
                                <SafeIcon icon={FiPlay} className="w-4 h-4" />
                              </>
                            ) : (
                              <>
                                <SafeIcon icon={FiCreditCard} className="w-4 h-4" />
                                <span>Enroll Now - ${finalPrice.toFixed(2)}</span>
                              </>
                            )}
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="space-y-3">
                        <Link
                          to="/login"
                          state={{from: {pathname: `/courses/${slug}`}}}
                          onClick={() => trackCTAClick('sign_in_to_enroll', 'course_detail', '/login')}
                          className="w-full bg-nutrition-600 hover:bg-nutrition-700 text-white py-3 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center"
                        >
                          Sign in to Enroll
                        </Link>
                        <Link
                          to="/register"
                          onClick={() => trackCTAClick('create_account', 'course_detail', '/register')}
                          className="w-full border border-nutrition-600 text-nutrition-600 hover:bg-nutrition-50 py-3 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center"
                        >
                          Create Account
                        </Link>
                      </div>
                    )}
                  </>
                )}

                <div className="mt-6 space-y-3">
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-nutrition-600" />
                    <span>Lifetime access</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-nutrition-600" />
                    <span>Certificate of completion</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-nutrition-600" />
                    <span>Downloadable resources</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-nutrition-600" />
                    <span>30-day money-back guarantee</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetail;